import { Injectable } from "@angular/core";
import { CustomProtocolService, EvaluationService, FilterBuilder, OrganizationProtocolPreference, ProtocolFilter, SampleType, Status, translateCommonErrorStatus } from "@core/data";
import { OrganizationProtocolPreferencesService } from "@core/data/api/organization-protocol-preferences.service";
import { Snackbar } from "@core/material";
import { Crop, Protocol } from "@library";
import { Action, Selector, State, StateContext } from "@ngxs/store";
import { tap } from "rxjs/operators";


export interface EvaluationTemplateFormStateModel {
    orgKey: string;
    sampleKeys: string[];
    status: Status;
    data: EvaluationTemplateData;
    preferences: OrganizationProtocolPreference[];
}

export interface EvaluationTemplateData {
    crop: Crop;
    type: SampleType;
    protocolOptions: Protocol[];
}

export interface EvaluationTemplateForm {
    protocol: string,
    measureLimit: number,
    crop: string,
    type: string
}

const DEFAULTS: EvaluationTemplateFormStateModel = {
    orgKey: null,
    sampleKeys: [],
    status: Status.UNINITIALIZED,
    data: null,
    preferences: [],
}

export class InitEvaluationTemplateForm {
    static readonly type = "[EvaluationTemplateForm] Init";
    constructor(public orgKey: string, public sampleKeys?: string[]) {}
}

export class SubmitEvaluationTemplateForm {
    static readonly type = "[EvaluationTemplateForm] Submit";
    constructor(public data: EvaluationTemplateForm[]) {}
}

export class LoadCropProtocols {
    static readonly type = "[EvaluationTemplateForm] Load Protocols";
    constructor(public crop: Crop, public type: SampleType) {}
}

export class LoadOrganisationPreference {
    static readonly type = '[EvaluationTemplateForm] Load Preference';
    constructor(public crop: Crop, public sampleType: SampleType) {}
}

@State<EvaluationTemplateFormStateModel>({
    name: 'evaluation_template_form',
    defaults: DEFAULTS
})
@Injectable()
export class EvaluationTemplateState {

    @Selector()
    static data(state: EvaluationTemplateFormStateModel) {
        return state.data;
    }

    constructor(
        private _snackbar: Snackbar,
        private _protocol_preferencesService: OrganizationProtocolPreferencesService,
        private _customProtocolService: CustomProtocolService,
        private _evalService: EvaluationService,
    ) { }

    @Action(InitEvaluationTemplateForm, {cancelUncompleted: true} )
    initEvaluationTemplateForm(ctx: StateContext<EvaluationTemplateFormStateModel>, action: InitEvaluationTemplateForm) {
        ctx.setState({
            ...DEFAULTS,
            orgKey: action.orgKey,
            status: Status.OK
        });
    }

    @Action(SubmitEvaluationTemplateForm)
    submitEvaluationTemplateForm(ctx: StateContext<EvaluationTemplateFormStateModel>, action: SubmitEvaluationTemplateForm) {
        return this._evalService.downloadEvaluationImportTemplate(action.data)
            .pipe(
                tap(
                    result => {
                        const url = window.URL.createObjectURL(result);
                        let anchor = document.createElement('a');
                        anchor.download = "evals-import-template";
                        anchor.href = url;
                        anchor.click();
                    },
                    error => {
                        console.warn('SampleIndexState: Evals Download Error ', error);
                        ctx.patchState({
                            status: translateCommonErrorStatus(error),
                        });
                    }
                )
            )
    }

    @Action(LoadOrganisationPreference)
    loadOrganisationPreference(ctx: StateContext<EvaluationTemplateFormStateModel>, action: LoadOrganisationPreference) {
        const state = ctx.getState();

        ctx.patchState({
            status: Status.LOADING,
        })

        const filter = (new FilterBuilder());

        filter.setQuery('ownerOrgKey', state.orgKey);

        return this._protocol_preferencesService.query(filter.get())
            .pipe(
                tap(
                    result => {
                        ctx.patchState({
                            preferences: result.data,
                        })

                        this.loadProtocolOptions(ctx, state.orgKey, result.data, action.crop, action.sampleType);
                    },
                    error => {
                        ctx.patchState({
                            preferences: state.preferences ? state.preferences : [],
                            status: translateCommonErrorStatus(error)
                        });
                    }
                )
            )
    }

    private loadProtocolOptions(ctx: StateContext<EvaluationTemplateFormStateModel>, orgKey: string, preferences: OrganizationProtocolPreference[], crop: Crop, sampleType: SampleType) {
        const state = ctx.getState();

        const filter: Partial<ProtocolFilter> = {
            showFavourites: true,
            showHidden: false,
            crops: [crop.id],
            sampleTypes: [sampleType],
        }

        this._customProtocolService.allProtocolsObserved(orgKey, preferences, filter).subscribe(all => {

            if (!all || all.length == 0) this._snackbar.error("No protocols found, contact your Organisation's Administrators to update your protocol preferences.")

            ctx.patchState({
                status: Status.OK,
                data: {
                    crop: crop,
                    type: sampleType,
                    protocolOptions: all,
                }
            })
        });
    }

}

